import React, { useState } from 'react';
import { API_URL } from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import './index.css';

const ContactAdmin = () => {
  const { user, loading: authLoading, signInWithGoogle, getAccessToken } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [classRequests, setClassRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState(null);

  // The API only returns this data to signed-in admins
  const fetchAdmin = async (path) => {
    const token = await getAccessToken();
    return fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  };

  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAdmin('/contact');
      const data = await response.json();

      if (response.ok) {
        setContacts(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch contacts');
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError('Network error: Could not connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassRequests = async () => {
    setRequestsLoading(true);
    setRequestsError(null);
    try {
      const response = await fetchAdmin('/class-requests');
      const data = await response.json();

      if (response.ok) {
        setClassRequests(data.data || []);
      } else {
        setRequestsError(data.message || 'Failed to fetch class requests');
      }
    } catch (err) {
      console.error('Error fetching class requests:', err);
      setRequestsError('Network error: Could not connect to server');
    } finally {
      setRequestsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading) {
    return <div className="contact-admin-page"><div className="container"><p className="page-subtitle">Checking your sign-in...</p></div></div>;
  }

  if (!user) {
    return (
      <div className="contact-admin-page">
        <div className="container">
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">Sign in with an admin Google account to continue.</p>
          <div className="admin-actions">
            <button className="fetch-button" onClick={() => signInWithGoogle('/admin-dashboard')}>
              Sign in with Google
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-admin-page">
      <div className="container">
        <h1 className="page-title">Admin Dashboard</h1>
        <p className="page-subtitle">View all contact form submissions</p>

        <div className="admin-actions">
          <button 
            className="fetch-button" 
            onClick={fetchContacts}
            disabled={loading}
          >
            {loading ? 'Fetching...' : 'Fetch Contacts'}
          </button>
          {contacts.length > 0 && (
            <div className="contacts-count">
              Total Contacts: {contacts.length}
            </div>
          )}
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {contacts.length > 0 && (
          <div className="contacts-table-wrapper">
            <table className="contacts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Education</th>
                  <th>Graduation Year</th>
                  <th>Purpose</th>
                  <th>Message</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact._id}>
                    <td>{contact.name}</td>
                    <td>
                      <a href={`mailto:${contact.emailAddress}`}>
                        {contact.emailAddress}
                      </a>
                    </td>
                    <td>
                      <a href={`tel:${contact.phoneNumber?.replace(/\s+/g, '')}`}>
                        {contact.phoneNumber}
                      </a>
                    </td>
                    <td>{contact.education}</td>
                    <td>{contact.graduationYear}</td>
                    <td>{contact.purpose || 'N/A'}</td>
                    <td className="message-cell">
                      {contact.message || 'N/A'}
                    </td>
                    <td>{formatDate(contact.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {contacts.length === 0 && !loading && !error && (
          <div className="no-contacts">
            <p>Click "Fetch Contacts" to load contact submissions</p>
          </div>
        )}

        <h2 className="page-title admin-section-title">Live Class Requests</h2>
        <p className="page-subtitle">Requests to join upcoming live cohorts</p>

        <div className="admin-actions">
          <button
            className="fetch-button"
            onClick={fetchClassRequests}
            disabled={requestsLoading}
          >
            {requestsLoading ? 'Fetching...' : 'Fetch Class Requests'}
          </button>
          {classRequests.length > 0 && (
            <div className="contacts-count">
              Total Requests: {classRequests.length}
            </div>
          )}
        </div>

        {requestsError && (
          <div className="error-message">
            {requestsError}
          </div>
        )}

        {classRequests.length > 0 && (
          <div className="contacts-table-wrapper">
            <table className="contacts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Course</th>
                  <th>Batch</th>
                  <th>Experience</th>
                  <th>Message</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {classRequests.map((request) => (
                  <tr key={request._id}>
                    <td>{request.name}</td>
                    <td>
                      <a href={`mailto:${request.emailAddress}`}>
                        {request.emailAddress}
                      </a>
                    </td>
                    <td>
                      <a href={`tel:${request.phoneNumber?.replace(/\s+/g, '')}`}>
                        {request.phoneNumber}
                      </a>
                    </td>
                    <td>{request.course}</td>
                    <td>{request.batch || 'N/A'}</td>
                    <td>{request.experience || 'N/A'}</td>
                    <td className="message-cell">
                      {request.message || 'N/A'}
                    </td>
                    <td>{request.status}</td>
                    <td>{formatDate(request.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {classRequests.length === 0 && !requestsLoading && !requestsError && (
          <div className="no-contacts">
            <p>Click "Fetch Class Requests" to load live class requests</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactAdmin;

